import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { IMultiStoreSelect } from 'src/app/common/models/multiStoreSelect';
import { AuthManager } from 'src/app/core/auth/auth.manager';
import { StoreHandoffService } from 'src/app/services/store-handoff.service';
import { HandoffInfo } from 'src/app/common/models/handoffInfo';
import { Operation } from 'src/app/common/models/operation';
import { TemporaryExceptionComponent } from '../../components/modal/temporary-exception/temporary-exception.component';
import { HandoffExceptionRequest } from 'src/app/common/models/handoffException';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { DeliveryMapComponent } from '../../components/delivery-map/delivery-map.component';
import { PolygonType } from '../../components/delivery-map/polygon-type.enum';
import { conditionallyLog } from 'src/app/common/consoleLogHelper';
import { LaunchDarklyService } from 'src/app/core/launchDarkly/launchDarkly.service';
import { PolygonHelpers } from 'src/app/common/polygonHelpers';
import { SchedulePlannedHandoffExceptionComponent } from '../../components/modal/schedule-planned-handoff-exception/schedule-planned-handoff-exception.component';
import { IPlannedHandoffException } from 'src/app/common/models/plannedHandoffException';
import { ViewPlannedHandoffExceptionComponent } from '../../components/modal/view-planned-handoff-exceptions/view-planned-handoff-exceptions.component';
import * as _ from 'lodash';
import {
  DeliveryMapChangeEvent,
  DeliveryMapChangeKinds,
} from '../../components/delivery-map/models/delivery-map-change-event.model';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatSelect, MatOption } from '@angular/material/select';
import { IncrementInputComponent } from '../../../modules/shared/components/increment-input/increment-input.component';
import { DayPipe, ShortDatePipe, TimeOnlyPipe } from '@pipes/time-date-pipe';
import { StatusService } from 'src/app/services/status.service';
import { MultiStoreComponent } from '../../components/modal/multi-store/multi-store.component';
import { combineLatest, interval, of, Subject } from 'rxjs';
import { switchMap, takeWhile, catchError, takeUntil, filter } from 'rxjs/operators';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import { AdminUIHelpers } from '@src/app/common/helpers';
import { HoursForDay, HoursUpdate } from '@src/app/common/models/hours-for-day';

@Component({
  templateUrl: './handoff-settings.component.html',
  styleUrls: ['./handoff-settings.component.scss'],
  standalone: true,
  imports: [
    MatSlideToggle,
    MatGridList,
    MatGridTile,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
    MatSelect,
    MatOption,
    IncrementInputComponent,
    DeliveryMapComponent,
    ShortDatePipe,
    DayPipe,
    TimeOnlyPipe,
  ],
})
export class HandoffSettingsComponent implements OnInit, OnDestroy {
  private _standardDeliveryMax = 12.5;
  private readonly destroy$ = new Subject<void>();
  public timeRange: any[] = AdminUIHelpers.getTimeRange();
  @ViewChild('deliveryMap') public deliveryMap: DeliveryMapComponent | undefined;

  // around the Rochester area
  private readonly defaultDeliveryCoordinates = [
    { latitude: 43.1, longitude: -77.51 },
    { latitude: 43.14, longitude: -77.55 },
  ];

  public PolygonType = PolygonType; // Expose enum to the template

  public get standardDeliveryMax(): number {
    return this._standardDeliveryMax;
  }

  private currentFacilityId: string | any;
  private handoffInfo!: HandoffInfo;
  private originalTablesideHours: HoursForDay[] = [];
  public tablesideHours: Record<string, any> = {
    changedState: [],
  };
  public storesSelected = 1;
  public authManager: AuthManager;
  public plannedHandoffExceptionsEnabled = false;
  public plannedDeliveryExceptionsEnabled = false;

  public handoffTypeViewModels: Record<string, any> = {};

  constructor(
    private _facilityManager: FacilityManager,
    private storeHandoffService: StoreHandoffService,
    private toastr: ToastrService,
    private readonly launchDarklyService: LaunchDarklyService,
    private authorizationServiceDI: AuthManager,
    public dialog: MatDialog,
    private ref: ChangeDetectorRef,
    private statusService: StatusService,
  ) {
    this.authManager = this.authorizationServiceDI;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.statusService.forceLoader = false;
  }

  public ngOnInit(): void {
    this.authManager.isLoggedIn$
      .pipe(
        filter((isAuthed) => isAuthed),
        switchMap(() =>
          combineLatest([this._facilityManager.facilityIdUpdates$, this._facilityManager.prepFacilitiesLoaded$]),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(([fId]) => {
        this.plannedHandoffExceptionsEnabled = this.launchDarklyService.PlannedHandoffExceptionsEnabled;
        this.plannedDeliveryExceptionsEnabled = this.launchDarklyService.PlannedDeliveryExceptions;
        this.resetHandoffTypeViewModels();
        this.loadTablesideHours(fId);
        this.refreshFacilityVariables(fId);
      });
  }

  private loadTablesideHours(fId: string): void {
    const days = this._facilityManager.prepFacilities().find((f) => f.facilityId === fId)?.tablesideHoursForDays ?? [];
    this.setOpenCloseIndexes(days);
  }

  private setOpenCloseIndexes(hoursForDays: HoursForDay[]): void {
    this.originalTablesideHours = hoursForDays.map((day) => ({
      ...day,
      openIndex: this.timeRange.findIndex((t) => t.time.isSame(day.openTime)),
      closeIndex: this.timeRange.findIndex((t) => t.time.isSame(day.closeTime)),
    }));
    this.tablesideHours.changedState = _.cloneDeep(this.originalTablesideHours);
  }

  public updateDeliveryMap(event: DeliveryMapChangeEvent): void {
    conditionallyLog('handling updateDeliveryMap', event);

    switch (event.kind) {
      case DeliveryMapChangeKinds.Standard:
        this.handoffTypeViewModels.delivery.standardAreaData = event.coordinates;
        this.dataStateChange(true, this.handoffTypeViewModels.delivery, 'standardAreaData');
        break;
      case DeliveryMapChangeKinds.Extended:
        this.handoffTypeViewModels.delivery.extendedAreaData = event.coordinates;
        this.dataStateChange(true, this.handoffTypeViewModels.delivery, 'extendedAreaData');
        break;
      case DeliveryMapChangeKinds.MovAll:
        this.handoffTypeViewModels.delivery.movPolygons = event.movPolygons;
        this.dataStateChange(true, this.handoffTypeViewModels.delivery, 'movPolygons');
        break;
      case DeliveryMapChangeKinds.ExtendedEnabled:
        this.handoffTypeViewModels.delivery.extendedDeliveryAreaEnabled = event.enabled;
        this.dataStateChange(true, this.handoffTypeViewModels.delivery, 'extendedDeliveryAreaEnabled');
        break;
    }
  }

  public resetDeliveryMap(): void {
    if (this.deliveryMap) {
      this.deliveryMap.resetPolygons();
      this.deliveryMap.setStandardCoordinates(this.defaultDeliveryCoordinates);
      this.deliveryMap.setExtendedCoordinates(this.defaultDeliveryCoordinates, false);
      this.deliveryMap.resetZipCodeOverlay();
      this.deliveryMap.resetZoom();
    }
  }

  private refreshFacilityVariables(facilityId: string): void {
    this.currentFacilityId = facilityId;
    this.resetSaveStates();
    if (this.deliveryMap) {
      this.resetDeliveryMap();
    }

    this.storeHandoffService.getStoreHandoffInformation(this.currentFacilityId).subscribe((f) => {
      this.handoffInfo = f;
      this.handoffTypeViewModels.inStore.handoffInstructions = f.inStorePickupInstructions;
      this.handoffTypeViewModels.inStore.handoffInstructionsBuffer = f.inStorePickupInstructions;
      this.handoffTypeViewModels.inStore.isAvailable = f.hasInStorePickup;

      this.handoffTypeViewModels.curbside.data = f.curbsideParkingSpots;
      this.handoffTypeViewModels.curbside.dataState = f.curbsideParkingSpots;
      this.handoffTypeViewModels.curbside.isAvailable = f.hasCurbside;
      this.handoffTypeViewModels.curbside.handoffInstructions = f.curbsidePickupInstructions;
      this.handoffTypeViewModels.curbside.handoffInstructionsBuffer = f.curbsidePickupInstructions;

      this.handoffTypeViewModels.delivery.isAvailable = f.hasDelivery;
      this.handoffTypeViewModels.delivery.handoffInstructions = f.deliveryInstructions;
      this.handoffTypeViewModels.delivery.handoffInstructionsBuffer = f.deliveryInstructions;

      this.handoffTypeViewModels.delivery.standardAreaData = f.standardDeliveryArea;
      this.handoffTypeViewModels.delivery.extendedAreaData = f.extendedDeliveryArea;
      this.handoffTypeViewModels.delivery.extendedDeliveryAreaEnabled = f.hasExtendedDeliveryArea;
      this.handoffTypeViewModels.delivery.standardAreaDataState = f.standardDeliveryArea;
      this.handoffTypeViewModels.delivery.extendedAreaDataState = f.extendedDeliveryArea;
      this.handoffTypeViewModels.delivery.extendedDeliveryAreaEnabledState = f.hasExtendedDeliveryArea;
      this.handoffTypeViewModels.delivery.movPolygons = f.movPolygons;

      this.deliveryMap?.setFacilityCoordinates(f.facilityCoordinates, `${f.facilityId} - ${f.facilityName}`);
      if (!f.extendedDeliveryArea || f.extendedDeliveryArea.length === 0) {
        const calculatedDefaultPolygon = PolygonHelpers.generateDefaultExtendedPolygon(f.facilityCoordinates);

        this.handoffTypeViewModels.delivery.standardAreaData = calculatedDefaultPolygon;
        this.handoffTypeViewModels.delivery.standardAreaDataState = calculatedDefaultPolygon;
        this.deliveryMap?.setExtendedCoordinates(calculatedDefaultPolygon, f.hasExtendedDeliveryArea);
      } else {
        this.deliveryMap?.setExtendedCoordinates(f.extendedDeliveryArea, f.hasExtendedDeliveryArea);
      }
      if (!f.standardDeliveryArea || f.standardDeliveryArea.length === 0) {
        const calculatedDefaultPolygon = PolygonHelpers.generateDefaultStandardPolygon(f.facilityCoordinates);

        this.handoffTypeViewModels.delivery.extendedAreaData = calculatedDefaultPolygon;
        this.handoffTypeViewModels.delivery.extendedAreaDataState = calculatedDefaultPolygon;
        this.deliveryMap?.setStandardCoordinates(calculatedDefaultPolygon);
      } else {
        this.deliveryMap?.setStandardCoordinates(f.standardDeliveryArea);
      }
      if (f.movPolygons && f.movPolygons.length > 0) {
        this.deliveryMap?.setMovPolygons(f.movPolygons);
      }

      this._standardDeliveryMax = this.getStandardDeliveryMax();

      this.handoffTypeViewModels.tableside.isAvailable = f.hasTableside;

      this.ref.detectChanges();
    });

    this.storeHandoffService.getStoreHandoffExceptions(this.currentFacilityId).subscribe((f) => {
      f.forEach((exception) => {
        switch (exception.handoffType) {
          case 'store': {
            this.handoffTypeViewModels.inStore.exceptions.push(exception);
            break;
          }
          case 'curbside': {
            this.handoffTypeViewModels.curbside.exceptions.push(exception);
            break;
          }
          case 'delivery': {
            this.handoffTypeViewModels.delivery.exceptions.push(exception);
            break;
          }
          default: {
            break;
          }
        }
      });
    });
  }

  private resetSaveStates(): void {
    this.handoffTypeViewModels.inStore.instructionSaveButton = false;
    this.handoffTypeViewModels.curbside.dataSaveButton = false;
    this.handoffTypeViewModels.curbside.instructionSaveButton = false;
    this.handoffTypeViewModels.delivery.dataSaveButton = false;
    this.handoffTypeViewModels.delivery.instructionSaveButton = false;
    this.handoffTypeViewModels.delivery.extendedDeliveryAreaEnabled = false;
  }

  public handoffTypeChangeEvent(handoffType: any): void {
    const patch: Operation[] = [];
    switch (handoffType.displayName) {
      case 'In-Store Pickup': {
        patch.push({
          op: 'replace',
          path: '/hasInStorePickup',
          value: this.handoffTypeViewModels.inStore.isAvailable,
        });
        break;
      }
      case 'Curbside Pickup': {
        patch.push({
          op: 'replace',
          path: '/hasCurbside',
          value: this.handoffTypeViewModels.curbside.isAvailable,
        });
        break;
      }
      case 'Tableside': {
        patch.push({
          op: 'replace',
          path: '/hasTableside',
          value: this.handoffTypeViewModels.tableside.isAvailable,
        });
        break;
      }
      default:
        break;
    }

    this.storeHandoffService.upsertStoreHandoffInformation(this.currentFacilityId, patch).subscribe({
      next: (f) => {
        this.refreshFacilityVariables(this.currentFacilityId);
        this.toastr.success('Handoff Type updated');
      },
      error: (e) => {
        this.toastr.error('Error while updating Handoff Type: ' + e);
        handoffType.isAvailable = !handoffType.isAvailable;
      },
    });
  }

  public deliveryToggleEvent(): void {
    const delivery = this.handoffTypeViewModels.delivery;
    const dialogRef = this.dialog.open(MultiStoreComponent, {
      width: '600px',
      maxHeight: '700px',
      maxWidth: '700px',
      data: { multiStoreData: [] as IMultiStoreSelect[] },
    });

    dialogRef.afterClosed().subscribe((result: IMultiStoreSelect[] | undefined) => {
      if (!result) {
        delivery.isAvailable = !delivery.isAvailable;
        return;
      }

      const selectedFacilityIds = result.filter((f) => f.isSelected).map((f) => +f.facilityId);

      if (selectedFacilityIds.length === 0) {
        delivery.isAvailable = !delivery.isAvailable;
        return;
      }

      const operations: Operation[] = [{ op: 'replace', path: '/hasDelivery', value: delivery.isAvailable }];

      this.statusService.forceLoader = true;

      this.storeHandoffService.bulkPatchHandoffInfo(selectedFacilityIds, operations).subscribe({
        next: (res) => {
          if (res && res.instanceId) {
            this.pollBulkHandoffStatus(res.instanceId, delivery);
          } else {
            this.statusService.forceLoader = false;
            this.toastr.error('Failed to start delivery update. Please try again.');
            delivery.isAvailable = !delivery.isAvailable;
          }
        },
        error: (e) => {
          this.statusService.forceLoader = false;
          this.toastr.error('Failed to start delivery update: ' + (e?.message || e?.statusText || 'Unknown error'));
          delivery.isAvailable = !delivery.isAvailable;
        },
      });
    });
  }

  private pollBulkHandoffStatus(instanceId: string, handoffType: any): void {
    interval(2000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.storeHandoffService.getBulkHandoffInfoStatus(instanceId)),
        takeWhile((response) => {
          return response.runtimeStatus === 'Running' || response.runtimeStatus === 'Pending';
        }, true),
        catchError((err) => {
          this.statusService.forceLoader = false;
          this.toastr.error('Lost connection while updating handoff information. Changes may still be processing.');
          handoffType.isAvailable = !handoffType.isAvailable;
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (!response) return;
        if (response.runtimeStatus === 'Completed') {
          this.statusService.forceLoader = false;
          const output = response.output;
          if (output && output.failureCount > 0) {
            this.toastr.warning(
              `Handoff information updated for ${output.successCount} store${output.successCount === 1 ? '' : 's'}, ${output.failureCount} failed`,
            );
          } else if (output) {
            this.toastr.success(
              `Handoff information updated for ${output.successCount} store${output.successCount === 1 ? '' : 's'}`,
            );
          } else {
            this.toastr.success('Handoff information updated');
          }
          this.refreshFacilityVariables(this.currentFacilityId);
        } else if (response.runtimeStatus === 'Failed' || response.runtimeStatus === 'Terminated') {
          this.statusService.forceLoader = false;
          this.toastr.error('Handoff information update failed. Please try again.');
          handoffType.isAvailable = !handoffType.isAvailable;
        } else if (response.runtimeStatus !== 'Running' && response.runtimeStatus !== 'Pending') {
          this.statusService.forceLoader = false;
          this.toastr.error('Handoff information update ended unexpectedly. Please try again.');
          handoffType.isAvailable = !handoffType.isAvailable;
        }
      });
  }

  public saveInstructions(handoffType: any): void {
    handoffType.handoffInstructions = handoffType.handoffInstructionsBuffer;
    const patch: Operation[] = [];
    const operation: Operation = {
      op: 'replace',
      path: '',
      value: handoffType.handoffInstructions,
    };

    switch (handoffType.displayName) {
      case 'In-Store Pickup': {
        operation.path = '/inStorePickupInstructions';
        break;
      }
      case 'Curbside Pickup': {
        operation.path = '/curbsidePickupInstructions';
        break;
      }
      case 'Delivery': {
        operation.path = '/deliveryInstructions';

        break;
      }
      default:
        break;
    }

    patch.push(operation);

    this.storeHandoffService.upsertStoreHandoffInformation(this.currentFacilityId, patch).subscribe({
      next: (r) => {
        this.instructionsToggle(handoffType);
        this.toastr.success(handoffType.displayName + ' Instructions updated!');
      },
      error: (e) => {
        this.toastr.error('Error while updating Instructions: ' + e);
      },
    });
  }

  public saveCurbsideData(): void {
    const patch: Operation[] = [
      {
        op: 'replace',
        path: '/curbsideParkingSpots',
        value: this.handoffTypeViewModels.curbside.data,
      },
    ];

    this.storeHandoffService.upsertStoreHandoffInformation(this.currentFacilityId, patch).subscribe({
      next: (r) => {
        this.dataToggle(this.handoffTypeViewModels.curbside);
        this.handoffInfo.curbsideParkingSpots = this.handoffTypeViewModels.curbside.data;
        this.toastr.success('Curbside spots updated!');
      },
      error: (e) => {
        this.toastr.error('Error while updating curbside spots: ' + e);
      },
      complete: () => {
        this.updateDataState(this.handoffTypeViewModels.curbside, 'data', this.handoffTypeViewModels.curbside.data);
      },
    });
  }

  public saveDeliveryData(): void {
    const facilitiesSelected: IMultiStoreSelect[] = [
      {
        facilityId: this.currentFacilityId,
        facilityName: this.handoffInfo.facilityName,
        isSelected: true,
      },
    ];
    this.saveDeliveryPolygonConfig(facilitiesSelected);
  }

  private saveDeliveryPolygonConfig(facilitiesSelected: IMultiStoreSelect[]): void {
    conditionallyLog('saveDeliveryPolygonConfig', facilitiesSelected);
    facilitiesSelected.forEach((f) => {
      const patch: Operation[] = [
        {
          op: 'replace',
          path: '/standardDeliveryArea',
          value: this.handoffTypeViewModels.delivery.standardAreaData,
        },
        {
          op: 'replace',
          path: '/extendedDeliveryArea',
          value: this.handoffTypeViewModels.delivery.extendedAreaData,
        },
        {
          op: 'replace',
          path: '/hasExtendedDeliveryArea',
          value: this.handoffTypeViewModels.delivery.extendedDeliveryAreaEnabled,
        },
        {
          op: 'replace',
          path: '/movPolygons',
          value: this.handoffTypeViewModels.delivery.movPolygons,
        },
      ];
      this.storeHandoffService.upsertStoreHandoffInformation(f.facilityId, patch).subscribe({
        next: (r) => {
          this.dataToggle(this.handoffTypeViewModels.delivery);
          this.storesSelected = 1;
          this.handoffInfo.standardDeliveryArea = this.handoffTypeViewModels.delivery.standardAreaData;
          this.handoffInfo.extendedDeliveryArea = this.handoffTypeViewModels.delivery.extendedAreaData;
          this.handoffInfo.hasExtendedDeliveryArea = this.handoffTypeViewModels.delivery.extendedDeliveryAreaEnabled;
          this.handoffInfo.movPolygons = this.handoffTypeViewModels.delivery.movPolygons;
          this.toastr.success('Delivery area for facility ' + f.facilityId + ' updated!');
        },
        error: (e) => {
          conditionallyLog('Error while updating facility delivery area:', e);
          this.toastr.error('Error while updating facility delivery area');
        },
        complete: () => {
          this.updateDataState(
            this.handoffTypeViewModels.delivery,
            'standardAreaData',
            this.handoffTypeViewModels.delivery.standardAreaData,
          );
          this.updateDataState(
            this.handoffTypeViewModels.delivery,
            'extendedAreaData',
            this.handoffTypeViewModels.delivery.extendedAreaData,
          );
          this.updateDataState(
            this.handoffTypeViewModels.delivery,
            'extendedDeliveryAreaEnabled',
            this.handoffTypeViewModels.delivery.extendedDeliveryAreaEnabled,
          );
          this.updateDataState(
            this.handoffTypeViewModels.delivery,
            'movPolygons',
            this.handoffTypeViewModels.delivery.movPolygons,
          );
        },
      });
    });
  }

  public openTimeSelected(selectedMoment: moment.Moment, dayOfWeek: number): void {
    this.tablesideHours.changedState[dayOfWeek].openTime = selectedMoment;
  }

  public closeTimeSelected(selectedMoment: moment.Moment, dayOfWeek: number): void {
    this.tablesideHours.changedState[dayOfWeek].closeTime = selectedMoment;
  }

  public hoursChanged(): boolean {
    return _.isEqual(this.tablesideHours.changedState, this.originalTablesideHours);
  }

  public cancelHours(): void {
    this.tablesideHours.changedState = _.cloneDeep(this.originalTablesideHours);
  }

  private buildHoursChangeList(): HoursUpdate[] {
    const changeList: HoursUpdate[] = [];
    this.tablesideHours.changedState.forEach((changedDay: HoursForDay, index: number) => {
      if (changedDay.openTime.isSame(this.originalTablesideHours[index].openTime, 'm') === false) {
        changeList.push({
          dateValue: changedDay.openTime,
          fieldName: 'openTime',
          index: index,
        });
      }
      if (changedDay.closeTime.isSame(this.originalTablesideHours[index].closeTime, 'm') === false) {
        changeList.push({
          dateValue: changedDay.closeTime,
          fieldName: 'closeTime',
          index: index,
        });
      }
    });
    return changeList;
  }

  private createHoursForDayPatch(index: number, value: moment.Moment, field: string): Operation {
    return {
      op: 'replace',
      path: `/tablesideHoursForDays/${index}/${field}`,
      value: value.format('HH:mm:ss'),
    };
  }

  public saveHours(): void {
    const patch = this.buildHoursChangeList().map((c) =>
      this.createHoursForDayPatch(c.index, c.dateValue, c.fieldName),
    );

    this._facilityManager.updateFacility(this.currentFacilityId, patch).subscribe({
      next: (updated) => {
        this.setOpenCloseIndexes(updated.tablesideHoursForDays);
        this.toastr.success('Hours updated');
      },
      error: () => this.toastr.error('Unable to update hours'),
    });
  }

  public instructionsToggle(handoffType: any): void {
    handoffType.instructionSaveButton = !handoffType.instructionSaveButton;
    handoffType.handoffInstructionsBuffer = handoffType.handoffInstructions;
  }

  public dataToggle(handoffType: any): void {
    handoffType.dataSaveButton = !handoffType.dataSaveButton;
  }

  public dataStateChange(event: boolean, handoffType: any, dataKey: string): void {
    conditionallyLog('dataStateChange', event, handoffType, dataKey);

    if (event === true) {
      handoffType.dataSaveButton = handoffType[dataKey] !== handoffType[dataKey + 'State'];
      this.ref.detectChanges();
    } else {
      this.toastr.warning(`Number input was out of allowed range: 1 to ${handoffType[dataKey + 'Max']}`);
    }
  }

  private updateDataState(handoffType: any, dataKey: string, data: any): void {
    handoffType[dataKey] = data;
    handoffType[dataKey + 'State'] = data;
  }

  public temporaryExceptionDialog(handoffType: any): void {
    const dialogRef = this.dialog.open(TemporaryExceptionComponent, {
      width: '400px',
      maxHeight: '700px',
      maxWidth: '700px',
      data: handoffType,
    });

    dialogRef.afterClosed().subscribe((result: HandoffExceptionRequest | any) => {
      if (result) {
        if (result.exceptionStatus === true) {
          handoffType.exceptions.push(result.handoffException);
        }
        if (result.exceptionStatus === false) {
          handoffType.exceptions = [];
        }
      }
    });
  }

  public schedulePlannedHandoffExceptionDialog(handoffType: any): void {
    const dialogRef = this.dialog.open(SchedulePlannedHandoffExceptionComponent, {
      width: '700px',
      maxHeight: '725px',
      maxWidth: '700px',
      data: { handoffType: handoffType.handoffType },
    });

    dialogRef.afterClosed().subscribe((result: IPlannedHandoffException) => {
      if (result) {
        conditionallyLog('Created Planned Handoff Exception', result);
        handoffType.plannedExceptions.push(result);
      }
    });
  }

  public viewPlannedHandoffExceptionDialog(handoffType: any): void {
    const dialogRef = this.dialog.open(ViewPlannedHandoffExceptionComponent, {
      width: '700px',
      maxHeight: '700px',
      maxWidth: '700px',
      data: handoffType,
    });

    dialogRef.afterClosed().subscribe((result: IPlannedHandoffException) => {
      if (result) {
        conditionallyLog('Created Planned Handoff Exception', result);
        handoffType.plannedExceptions.push(result);
      }
    });
  }

  private getStandardDeliveryMax(): number {
    return Math.min(this.handoffTypeViewModels.delivery.dataMax, this.handoffTypeViewModels.delivery.extendedData);
  }

  public isPlannedHandoffExceptionEnabled(): boolean {
    return this.plannedHandoffExceptionsEnabled;
  }

  public isPlannedDeliveryException(): boolean {
    return this.plannedDeliveryExceptionsEnabled;
  }

  private resetHandoffTypeViewModels(): void {
    this.handoffTypeViewModels = {
      inStore: {
        handoffType: 'store',
        displayName: 'In-Store Pickup',
        data: null,
        dataMax: 0,
        extendedData: null,
        dataSaveButton: false,
        instructionSaveButton: false,
        isAvailable: false,
        handoffInstructions: '',
        handoffInstructionsBuffer: '',
        exceptions: [],
      },
      curbside: {
        handoffType: 'curbside',
        displayName: 'Curbside Pickup',
        data: null,
        dataMax: 20,
        extendedData: null,
        dataSaveButton: false,
        instructionSaveButton: false,
        isAvailable: false,
        handoffInstructions: '',
        handoffInstructionsBuffer: '',
        exceptions: [],
        plannedExceptions: [],
      },
      delivery: {
        handoffType: 'delivery',
        displayName: 'Delivery',
        data: null,
        dataMax: 12.5,
        extendedData: null,
        extendedDataMax: 100,
        dataSaveButton: false,
        instructionSaveButton: false,
        isAvailable: false,
        handoffInstructions: '',
        handoffInstructionsBuffer: '',
        exceptions: [],
        standardAreaData: [],
        extendedAreaData: [],
        movPolygons: [],
        extendedDeliveryAreaEnabled: false,
        plannedExceptions: [],
      },
      tableside: {
        handoffType: 'tableside',
        displayName: 'Tableside',
      },
    };
  }
}
